import { useLocation } from "react-router-dom";
import { useBilling } from "./use-billing";
import { useWorkspacePreferences } from "./use-workspace-preferences";
import { isPreferenceEnabled } from "@/utils/workspace-preferences";
import { isLovablePreview } from "@/lib/utils/environment";
import { SubscriptionStatus, type Subscription } from "@/lib/graphql/types-and-hooks";

// PastDue is intentionally excluded: Stripe keeps retrying the charge during
// past_due, so we keep the user inside the app and surface the failed payment
// via PastDuePaymentBanner + the Settings "Payment Failed" card.
const BAD_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.Canceled,
  SubscriptionStatus.Paused,
  SubscriptionStatus.Unpaid,
  SubscriptionStatus.IncompleteExpired,
]);

// Paths that must remain reachable even when the subscription is broken,
// so admins can self-serve recovery and users can sign out.
const ALLOWED_PATHS = [
  "/subscription-expired",
  "/plans",
  "/settings",
  "/login",
  "/callback",
  "/onboarding",
  "/onboard",
  "/setup",
  "/stripe/callback",
  "/choose-workspace",
  "/shared/",
];

export interface SubscriptionGuardResult {
  shouldRedirect: boolean;
}

function shouldRedirectFor(
  subscription: Subscription | null | undefined,
  trialExpiredFlag: boolean,
): boolean {
  if (subscription && BAD_STATUSES.has(subscription.status)) return true;

  if (
    trialExpiredFlag &&
    (!subscription ||
      (subscription.status !== SubscriptionStatus.Active &&
        subscription.status !== SubscriptionStatus.Incomplete))
  ) {
    return true;
  }

  return false;
}

export function useSubscriptionGuard(): SubscriptionGuardResult {
  const location = useLocation();
  const { subscription, loading } = useBilling();
  const { workspacePreferences, loading: prefsLoading } = useWorkspacePreferences();

  if (isLovablePreview()) return { shouldRedirect: false };
  if (loading || prefsLoading) return { shouldRedirect: false };

  if (ALLOWED_PATHS.some(p => location.pathname.startsWith(p))) {
    return { shouldRedirect: false };
  }

  const trialExpiredFlag = !!isPreferenceEnabled("trial_expired", workspacePreferences);
  return { shouldRedirect: shouldRedirectFor(subscription, trialExpiredFlag) };
}

export { BAD_STATUSES, shouldRedirectFor };
